import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { LINKS } from './constants/links';
import React, { useEffect, useState } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer, toast } from 'react-toastify';
import Home from './pages/Home';
import Overview from './pages/Overview';
import Deployments from './pages/Deployments';
import Signin from './pages/Signin';
import Signup from './pages/Signup';
import Blog from './pages/Blog';
import Contact from './pages/Contact';
import Download from './pages/Download';
import NotFound from './pages/NotFound';
import NavBar from './components/NavBar';
import Footer from './components/Footer';
import License from './pages/License';
import Submission from './pages/Submission';
import VerifyEmail from './pages/VerifyEmail';
import DeploymentLogsTable from './pages/DeploymentLogsTable';
import Profile from './pages/Profile';
import { UserProvider, useUser } from './context/UserContext';
import ResetPassword from './pages/ResetPassword';
import { useScrollToHash } from './hooks/useScrollToHash';

function AppContent() {
  const [isDark, setIsDark] = useState(
    () =>
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const { isAuthenticated, setIsAuthenticated, setUser, user } = useUser();

  const handleLogout = () => {
    sessionStorage.removeItem('token');
    setIsAuthenticated(false);
    setUser(null);
    toast.info('You have been logged out.');
    navigate('/');
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => setIsDark(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const originalConsoleError = console.error;
      console.error = function (...args) {
        toast.error(`Error: ${args.join(' ')}`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        originalConsoleError.apply(console, args);
      };
      return () => {
        console.error = originalConsoleError;
      };
    }
  }, []);

  useScrollToHash();

  return (
    <div className="flex flex-col min-h-screen bg-white dark:bg-neutral-900 text-gray-900 dark:text-white">
      <NavBar
        isDark={isDark}
        setIsDark={setIsDark}
        isAuthenticated={isAuthenticated}
        onLogout={handleLogout}
      />
      <main className={`flex-grow ${!isHomePage ? 'pt-24' : ''}`}>
        <Routes>
          <Route path={LINKS.HOME} element={<Home />} />
          <Route path={LINKS.OVERVIEW} element={<Overview />} />
          <Route path={LINKS.BLOG} element={<Blog />} />
          <Route path={LINKS.BLOG_ELEMENT} element={<Blog />} />
          <Route
            path={LINKS.DEPLOYMENTS}
            element={<Deployments isDark={isDark} />}
          />
          <Route path={LINKS.CONTACT} element={<Contact />} />
          <Route path={LINKS.DOWNLOAD} element={<Download />} />
          <Route
            path={LINKS.SUBMISSION}
            element={<Submission isDark={isDark} />}
          />
          <Route path={LINKS.SIGNIN} element={<Signin />} />
          <Route path={LINKS.EMAIL} element={<VerifyEmail />} />
          <Route path={LINKS.RESET_PASSWORD} element={<ResetPassword />} />
          <Route path={LINKS.SIGNUP} element={<Signup />} />
          <Route path={LINKS.LICENSE} element={<License />} />
          <Route
            path={LINKS.DEPLOYMENT_LOGS}
            element={<DeploymentLogsTable />}
          />
          <Route
            path={LINKS.PROFILE}
            element={<Profile onLogout={handleLogout} />}
          />
          <Route path={LINKS.NOT_FOUND} element={<NotFound />} />
        </Routes>
      </main>
      <Footer isDark={isDark} />
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <ToastContainer />
      <UserProvider>
        <AppContent />
      </UserProvider>
    </Router>
  );
}
